import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { ImageCarousel } from "@components/image_carousel";
import { Loader } from "@components/Loader";
import { ModalDialog } from "@components/modal_dialog";
import type { ListingImage } from "@gql";

interface Props {
  images: ListingImage[] | undefined;
  propertyListingId: string | undefined;
}

interface PropertyListingChannelData {
  images: ListingImage[] | undefined;
}

const PropertyListingImages = ({ images, propertyListingId }: Props) => {
  const [imagesState, setImagesState] = useState<ListingImage[] | undefined>(images || []);
  const [loading, setLoading] = useState(!images || images.length === 0);
  const [displayModal, setDisplayModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (!imagesState || (imagesState && imagesState.length === 0)) {
      // @ts-expect-error ActionCable doesn't have types
      const cable = ActionCable.createConsumer();
      const newSubscription = cable.subscriptions.create(
        {
          channel: "PropertyListingChannel",
          id: propertyListingId,
        },
        {
          received: (data: PropertyListingChannelData) => {
            // @ts-expect-error ActionCable doesn't have types
            subscription.unsubscribe;
            setImagesState(data?.images || []);
            setSubscription(null);
            setLoading(false);
          },
          connected: () => {
            console.log("Connected!");
          },
        },
      );

      setSubscription(newSubscription);
    }
  }, []);

  const handleImageClicked = (newSelectedIndex: number) => {
    setDisplayModal(true);
    setSelectedIndex(newSelectedIndex);
  };

  const renderImages = () => {
    if (!images || images.length === 0) return null;

    return (
      <div>
        <ImageCarousel images={images} onImageClicked={handleImageClicked} />
        <ModalDialog display={displayModal} onClosed={() => setDisplayModal(false)}>
          <ImageCarousel
            activateKeyboardShortcuts
            images={images}
            selectedIndex={selectedIndex}
            responsive={{
              0: {
                items: 1,
                loop: true,
                autoWidth: false,
              },
            }}
          />
        </ModalDialog>
      </div>
    );
  };

  const renderNoImages = () => {
    if (!images || (images?.length === 0 && loading === false)) {
      return <div className="no-images text-center">No images available</div>;
    }
  };

  return (
    <div id="property-listing-images-component">
      {renderImages()}
      {renderNoImages()}
      <Loader loading={loading} text="Loading images" />
    </div>
  );
};

document.addEventListener("DOMContentLoaded", () => {
  const element = document.getElementById("property-listing-images-content");

  if (element) {
    const root = createRoot(element);

    root.render(
      <PropertyListingImages
        images={JSON.parse(element.dataset?.images || "[]")}
        propertyListingId={element.dataset.propertyListingId}
      />,
    );
  }
});

export { PropertyListingImages };
